import { Css, IconButton, Palette, RouteTabWithContent, TabsWithContent } from "@homebound/beam";
import { useMemo } from "react";
import { addEntityParam, createTradePartnersUrl } from "src/RouteUrls";
import { TradePartnerOnboardingStatus } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { PageHeader } from "src/routes/layout/PageHeader";
import { IdOrAddParams } from "src/routes/routesDef";
import { TradePartnerCommitmentsTab } from "src/routes/trade-partners/TradePartnerCommitmentsTab";
import { TradePartnerContactsTab } from "src/routes/trade-partners/TradePartnerContactsTab";
import { TradePartnerDetailsTab } from "src/routes/trade-partners/TradePartnerDetailsTab";
import {
  TradePartnerPageTradePartnerFragment,
  useSaveTradePartnerMutation,
  useTradePartnerPageQuery,
} from "src/routes/trade-partners/TradePartnerPage.generated";
import { TradePartnerReviews } from "src/routes/trade-partners/components/TradePartnerReviews";
import * as tradePartnersPaths from "src/routing/domains/tradePartnersPaths";
import { queryResult } from "src/utils/queryResult";
import { TradePartnerDevelopmentContractsTab } from "./TradePartnerDevelopmentContractsTab";
import { TradePartnerRequirements } from "./TradePartnerRequirementsTab";

export function TradePartnerPage() {
  const { idOrAdd } = useRouteParams<IdOrAddParams>();
  const isNew = idOrAdd === addEntityParam;
  const query = useTradePartnerPageQuery({ variables: { id: idOrAdd }, skip: isNew });

  if (isNew) {
    return <DataView tradePartner={undefined} />;
  }

  return queryResult(query, ({ tradePartner }) => {
    return <DataView tradePartner={tradePartner} />;
  });
}

function DataView({ tradePartner }: { tradePartner: TradePartnerPageTradePartnerFragment | undefined }) {
  // Only used for updating the sweetheart indicator, which we put in the header.
  const [saveTradePartner] = useSaveTradePartnerMutation();

  const tabs = useMemo(
    () => createTabs(tradePartner?.id, tradePartner?.onboardingStatus.code),
    [tradePartner?.id, tradePartner?.onboardingStatus.code],
  );

  return (
    <>
      <PageHeader
        title={tradePartner?.name || "New Trade Partner"}
        breadcrumb={{ label: "All Trade Partners", href: createTradePartnersUrl() }}
        left={
          tradePartner && (
            <IconButton
              icon="star"
              color={tradePartner.isSweetheart ? Palette.Gray700 : Palette.Gray300}
              onClick={async () => {
                await saveTradePartner({
                  variables: { input: { id: tradePartner.id, isSweetheart: !tradePartner.isSweetheart } },
                });
              }}
            />
          )
        }
      />
      <div css={Css.mt2.$}>
        <TabsWithContent tabs={tabs} />
      </div>
    </>
  );
}

function createTabs(
  tradePartnerId: string | undefined,
  onboardingStatus: TradePartnerOnboardingStatus | undefined,
): RouteTabWithContent[] {
  const isNew = tradePartnerId === undefined;
  const idOrAdd = tradePartnerId || addEntityParam;
  return [
    {
      name: "Details",
      href: tradePartnersPaths.tradePartnerRoute.url({ idOrAdd }),
      path: tradePartnersPaths.tradePartnerRoute.path,
      render() {
        return <TradePartnerDetailsTab idOrAdd={idOrAdd} />;
      },
    },
    {
      name: "Contacts",
      href: tradePartnersPaths.tradePartnerContactsRoute.url({ idOrAdd }),
      path: tradePartnersPaths.tradePartnerContactsRoute.path,
      disabled: isNew,
      render() {
        return <TradePartnerContactsTab tradePartnerId={idOrAdd} onboardingStatus={onboardingStatus} />;
      },
    },
    {
      name: "Reviews",
      href: tradePartnersPaths.tradePartnerReviewsRoute.url({ idOrAdd }),
      path: tradePartnersPaths.tradePartnerReviewsRoute.path,
      disabled: isNew,
      render() {
        return <TradePartnerReviews tradePartnerId={idOrAdd} />;
      },
    },
    {
      name: "Commitments",
      href: tradePartnersPaths.tradePartnerCommitmentsRoute.url({ idOrAdd }),
      path: tradePartnersPaths.tradePartnerCommitmentsRoute.path,
      disabled: isNew,
      render() {
        return <TradePartnerCommitmentsTab tradePartnerId={idOrAdd} />;
      },
    },
    {
      name: "Development Contracts",
      href: tradePartnersPaths.tradePartnerDevelopmentContractsRoute.url({ idOrAdd }),
      path: tradePartnersPaths.tradePartnerDevelopmentContractsRoute.path,
      disabled: isNew,
      render() {
        return <TradePartnerDevelopmentContractsTab tradePartnerId={idOrAdd} />;
      },
    },
    {
      name: "Requirements",
      href: tradePartnersPaths.tradePartnerRequirementsRoute.url({ idOrAdd }),
      path: tradePartnersPaths.tradePartnerRequirementsRoute.path,
      disabled: isNew,
      render() {
        return <TradePartnerRequirements tradePartnerId={idOrAdd} />;
      },
    },
  ];
}
